import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Alert } from '../../../components/Alert/Alert';
import { isDefined } from '../../../utilities/isDefined';

export interface ErrorMessageRegistry {
  registerMessage: (id: string, message: ReactNode) => void;
  unregisterMessage: (id: string) => void;
}

export const ErrorMessageRegistryContext = createContext<ErrorMessageRegistry>(null as any);

/**
 * Maintains a collection of error messages meant render validation errors
 * associated with nested form fields outside of their parent's DOM.
 */
export const useErrorMessages = () => {
  const [messages, setMessages] = useState<Record<string, ReactNode>>({});

  const registerMessage = useCallback(
    (id: string, message: ReactNode) => {
      setMessages((prev) => ({ ...prev, [id]: message }));
    },
    [setMessages],
  );

  const unregisterMessage = useCallback(
    (id: string) => {
      setMessages((prev) => ({ ...prev, [id]: undefined }));
    },
    [setMessages],
  );

  return { messages, registerMessage, unregisterMessage };
};

/**
 * Registers an error message with the ErrorMessageRegistry, to be rendered via
 * the ErrorMessages component. It's worth noting that this can cause the
 * ErrorMessage to render to DOM after the initial render and may lead to quirky
 * behavior.
 *
 * Note: Be careful with this component as it can create infinite render loops
 * if its `id` or `errorMessage` props change on each render!
 */
export function ErrorMessageFakePortal({
  errorMessage,
  id,
}: {
  id: string;
  errorMessage: ReactNode;
}) {
  const { registerMessage, unregisterMessage } = useContext(ErrorMessageRegistryContext);

  useEffect(() => {
    registerMessage(
      id,
      <Alert id={id} type="inline" relation="stacked" variant="negative" heading={errorMessage} />,
    );

    return () => unregisterMessage(id);
  }, [id, errorMessage, registerMessage, unregisterMessage]);

  return null;
}

function getMessageText(message: unknown): string | unknown {
  // Extracts the heading prop out of an Alert react element object
  if (
    !!message &&
    typeof message === 'object' &&
    'props' in message &&
    !!message.props &&
    typeof message.props === 'object' &&
    'heading' in message.props &&
    typeof message.props.heading === 'string'
  ) {
    return message.props.heading;
  }

  return message;
}

/**
 * Renders a list of error messages.
 */
export function ErrorMessages({
  messages,
  deduplicate = true,
}: {
  messages: Record<string, ReactNode>;
  deduplicate?: boolean;
}) {
  const messagesToDisplay = useMemo(() => {
    const m = Object.values(messages).filter(isDefined);

    if (deduplicate) {
      const seen = new Set<unknown>();
      return m.filter((message) => {
        const messageText = getMessageText(message);
        if (seen.has(messageText)) return false;
        seen.add(messageText);
        return true;
      });
    }

    return m;
  }, [messages, deduplicate]);

  return (
    <>
      {messagesToDisplay.map((message, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>{message}</React.Fragment>
      ))}
    </>
  );
}
